// Tiering for the portal homework section (#414 contract § Rider amendment 6,
// operator-requested 2026-08-09): most recent by default, "show earlier
// homework" for finished current-semester work beyond the recent window,
// "show earlier semesters" for previous semesters.
//
// Pure function so the tier math is testable without the DOM. Rules:
// - Sort is newest-first by due_date, falling back to the created_at DATE
//   prefix — most live assignments are created against a session with NO due
//   date, and undated work must order by recency, not sink to the bottom.
// - "Current semester" anchors on the greatest (semester_start_date,
//   semester_id) tuple present — the SAME ordering the server and the billing
//   surface use — NEVER on due dates. Assignments with no semester_id
//   (deploy skew, or a deleted class row) count as CURRENT: unknown
//   provenance must never hide in the previous-semesters tier.
// - FAIL-CLOSED PIN: every ACTIONABLE current-semester assignment is always
//   in the default view, wherever it sorts — the recent window only governs
//   FINISHED work. "Actionable" is the same predicate the student portal's
//   upload widget renders for; the pin and the action slot must never
//   disagree about what a student still owes. Previous semesters are closed
//   business and collapse entirely.
import { currentSemesterAnchor, isCurrentSemester } from './semesters'

export const RECENT_HOMEWORK_COUNT = 3

const ACTIONABLE_STATUSES = new Set(['not_submitted', 'returned'])

function sortKey(assignment) {
  if (assignment?.due_date) return assignment.due_date
  const created = assignment?.created_at
  return typeof created === 'string' ? created.slice(0, 10) : ''
}

// Wire spelling for this surface: assignments arrive straight off the server
// payload in snake_case. The ordering rule itself lives in shared/semesters.
function semesterTuple(assignment) {
  return [assignment?.semester_start_date || '', assignment?.semester_id || '']
}

export function assignmentIsActionable(assignment) {
  // Same precedence as ParentLayout.helpers::assignmentStatus: a real upload
  // status wins; only then does a teacher's classroom check (#597, feedback
  // homework score at the due session) settle a row with no upload.
  const status = assignment?.submission?.status
  if (status && status !== 'not_submitted') return ACTIONABLE_STATUSES.has(status)
  // A teacher verdict settles the row: a score, or (2026-09) the explicit
  // "not submitted" — the red chip is the outcome, there is no upload owed.
  if (assignment?.classroom_check?.score != null) return false
  if (assignment?.classroom_check?.notSubmitted) return false
  return ACTIONABLE_STATUSES.has('not_submitted')
}

export function tierAssignments(assignments) {
  const sorted = [...(assignments || [])]
    .sort((a, b) => sortKey(b).localeCompare(sortKey(a)))
  if (!sorted.length) return { recent: [], earlier: [], previous: [] }

  const anchor = currentSemesterAnchor(sorted, semesterTuple)
  const isCurrent = a => isCurrentSemester(a, anchor, semesterTuple)
  const current = sorted.filter(isCurrent)
  const previous = sorted.filter(a => !isCurrent(a))

  const window = new Set(
    current.slice(0, RECENT_HOMEWORK_COUNT).map(a => a.id),
  )
  const recent = current.filter(
    a => window.has(a.id) || assignmentIsActionable(a),
  )
  const recentIds = new Set(recent.map(a => a.id))
  const earlier = current.filter(a => !recentIds.has(a.id))
  return { recent, earlier, previous }
}
